<template>
  <div id="IssuanceView" class="d-flex flex-column h-100 overflow-hidden">
    <div class="flex-grow-1 overflow-auto">
      <div class="container py-4">
        <div class="mb-4">
          <span
            class="text-uppercase text-secondary fw-bold fs-10 letter-spacing-eyebrow"
          >
            Admin · Issuance
          </span>
          <h1 class="mt-1 mb-0">Issuance dashboard</h1>
        </div>

        <div class="d-flex flex-wrap align-items-start gap-3 mb-3">
          <IssuanceBirdseyeGrid
            :data="birdseye"
            :loading="birdseyeLoading"
            :active-doc-country="docCountry"
            :doc-countries="docCountries"
            class="issuance-birdseye-flex-item"
            :class="{ 'flex-grow-1': !docCountry }"
            @select="onBirdseyeSelect"
            @select-country="toggleDocCountry"
          />
          <IssuanceCountryDoc
            v-if="docCountry"
            :country="docCountry"
            class="issuance-doc-panel flex-grow-1"
            @close="docCountry = null"
          />
        </div>

        <div class="d-flex flex-wrap align-items-center gap-2 mb-3">
          <div class="issuance-search position-relative flex-grow-1">
            <font-awesome-icon
              icon="fa-solid fa-magnifying-glass"
              class="issuance-search__icon position-absolute top-50 translate-middle-y text-secondary fs-10"
            />
            <input
              v-model="search"
              class="form-control form-control-sm ps-5"
              placeholder="Search company name..."
            />
          </div>
          <select
            v-model="countryFilter"
            class="form-select form-select-sm issuance-country-select"
          >
            <option value="">All countries</option>
            <option
              v-for="opt in availableFilters"
              :key="opt.key"
              :value="opt.key"
            >
              {{ opt.label }}
            </option>
          </select>
          <div class="issuance-seg">
            <button
              v-for="option in sortOptions"
              :key="option.value"
              type="button"
              :class="{ active: sortField === option.value }"
              :aria-pressed="sortField === option.value"
              @click="setSort(option.value)"
            >
              {{ option.label }}
              <font-awesome-icon
                v-if="sortField === option.value"
                :icon="
                  sortDirection === 'asc'
                    ? 'fa-solid fa-arrow-up'
                    : 'fa-solid fa-arrow-down'
                "
                class="ms-1 fs-10"
              />
            </button>
          </div>
          <div class="flex-grow-1"></div>
          <IssuanceMonthPicker v-model:year="year" v-model:month="month" />
          <span class="text-secondary fs-9">
            Showing
            <strong class="text-body">{{ visibleCompanies.length }}</strong>
            of {{ rawCompanies.length }}
          </span>
        </div>

        <div
          v-if="loading"
          class="issuance-summary issuance-summary--loading mb-3 text-center text-secondary"
        >
          Loading...
        </div>
        <div v-else class="issuance-summary mb-3">
          <div class="issuance-summary__row">
            <div
              class="issuance-summary__label text-uppercase text-secondary fw-bold fs-10 letter-spacing-eyebrow"
            >
              Total · {{ visibleCompanies.length }} compan{{
                visibleCompanies.length === 1 ? "y" : "ies"
              }}
            </div>
            <div class="issuance-summary__metric">
              <span class="text-secondary fs-10 d-block text-end"
                >Expected</span
              >
              <span class="tabular-nums"
                >{{ formatMwh(summary.expectedMwh) }} MWh</span
              >
            </div>
            <div class="issuance-summary__metric">
              <span class="text-secondary fs-10 d-block text-end">Issued</span>
              <span class="tabular-nums"
                >{{ formatMwh(summary.issuedMwh) }} MWh</span
              >
            </div>
            <div class="issuance-summary__metric">
              <span class="text-secondary fs-10 d-block text-end"
                >Issued devices</span
              >
              <span class="tabular-nums"
                >{{ summary.issuedDeviceCount }} /
                {{ summary.eligibleDeviceCount }}</span
              >
              <span
                v-if="summary.smallDeviceCount > 0"
                class="text-secondary fs-10 d-block text-end"
                :title="`${summary.smallDeviceCount} small device(s): ${summary.smallIssuedCount} issued — counted in MWh but not in issued%`"
              >
                +{{ summary.smallDeviceCount }} small
              </span>
            </div>
            <div class="issuance-summary__bar">
              <span
                class="issuance-summary__bar-label text-secondary fs-10 text-start"
                >Issued devices %</span
              >
              <StackedProgressBar
                :segments="summarySegments"
                :total="100"
                aria-label="Devices issued percent"
              />
              <strong class="tabular-nums">
                <template v-if="summary.eligibleDeviceCount === 0">—</template>
                <template v-else
                  >{{ formatPercent(summary.devicesIssuedPercent) }}%</template
                >
              </strong>
            </div>
            <button
              type="button"
              class="btn btn-sm btn-phoenix-secondary text-nowrap"
              :disabled="exporting"
              title="Excel summary of devices with no issuance in the selected month"
              @click="downloadDevicesWithoutIssuance"
            >
              <font-awesome-icon
                :icon="
                  exporting ? 'fa-solid fa-spinner' : 'fa-solid fa-download'
                "
                :spin="exporting"
                class="me-2"
              />
              Problems
            </button>
          </div>
        </div>

        <div v-if="loading" class="text-center text-secondary py-5">
          Loading...
        </div>
        <div
          v-else-if="hasDashboardError"
          class="border border-dashed rounded-3 text-center text-danger py-5 bg-body"
        >
          Couldn't load the issuance dashboard.
          <button
            type="button"
            class="btn btn-sm btn-link p-0 ms-1 align-baseline"
            @click="fetchDashboard"
          >
            Retry
          </button>
        </div>
        <div
          v-else-if="visibleCompanies.length === 0"
          class="border border-dashed rounded-3 text-center text-secondary py-5 bg-body"
        >
          No companies match the current filter.
        </div>
        <div v-else class="d-flex flex-column gap-2">
          <div
            class="issuance-table-header text-uppercase text-secondary fw-bold fs-10 letter-spacing-eyebrow"
          >
            <span></span>
            <span>Country</span>
            <span>Company</span>
            <span class="text-end">Expected</span>
            <span class="text-end">Issued</span>
            <span class="text-end">Issued devices</span>
            <span>Issued devices %</span>
          </div>
          <IssuanceCompanyRow
            v-for="company in visibleCompanies"
            :key="companyRowKey(company)"
            :company="company"
            :expanded="expandedKeys.has(companyRowKey(company))"
            @toggle="toggleExpanded(companyRowKey(company))"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import IssuanceBirdseyeGrid from "@/admin/issuance/components/IssuanceBirdseyeGrid.vue";
import IssuanceCompanyRow from "@/admin/issuance/components/IssuanceCompanyRow.vue";
import IssuanceCountryDoc from "@/admin/issuance/components/IssuanceCountryDoc.vue";
import { fetchIssuanceDocCountries } from "@/admin/issuance/issuanceDocs";
import IssuanceMonthPicker from "@/admin/issuance/components/IssuanceMonthPicker.vue";
import {
  companyRowKey,
  filterKeyOf,
  formatMwh,
  formatPercent,
  pickVariant,
  registryShortLabel,
  sortCompanies,
  summarize,
  type SortDirection,
  type SortField,
} from "@/admin/issuance/issuanceMetrics";
import StackedProgressBar from "@/components/common/StackedProgressBar.vue";
import type { StackedProgressBarSegment } from "@/components/common/stackedProgressBarTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { goSolidApi } from "@/composables/useGoSolidApi";
import axios from "axios";
import type {
  IssuanceBirdseyeResponse,
  IssuanceDashboardCompany,
  IssuanceDashboardResponse,
} from "@/types";
import { getFlagEmoji, translateCountryCode } from "@/utils/countries";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({
  name: "AdminIssuanceView",
});

const { t, te } = useI18n();
const { globalErrorHandler } = useGlobalHandlers();

const previousMonth = (): { year: number; month: number } => {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return { year: date.getFullYear(), month: date.getMonth() + 1 };
};

const initial = previousMonth();
const year = ref(initial.year);
const month = ref(initial.month);

const rawCompanies = ref<IssuanceDashboardCompany[]>([]);
const loading = ref(false);
const hasDashboardError = ref(false);
// Aborted on each new fetch so a fast year/month change can't leave a stale response
// in flight that overwrites the latest selection's data.
let dashboardAbortController: AbortController | null = null;

const birdseye = ref<IssuanceBirdseyeResponse | null>(null);
const birdseyeLoading = ref(false);

// Country whose issuance ops notes are open next to the bird's-eye grid, and
// the set of countries that have notes (drives the grid's doc icons).
const docCountry = ref<string | null>(null);
const docCountries = ref<string[]>([]);

const toggleDocCountry = (country: string): void => {
  docCountry.value = docCountry.value === country ? null : country;
};

const exporting = ref(false);

const search = ref("");
const countryFilter = ref("");
const sortField = ref<SortField>("expected");
const sortDirection = ref<SortDirection>("desc");

// Keyed by companyRowKey (id + registry) — the same company.id can appear on multiple
// registries, and each row must expand independently.
const expandedKeys = reactive<Set<string>>(new Set());

const sortOptions: { value: SortField; label: string }[] = [
  { value: "country", label: "Country" },
  { value: "name", label: "Name" },
  { value: "expected", label: "Expected MWh" },
  { value: "issued", label: "Issued %" },
];

const setSort = (field: SortField): void => {
  if (sortField.value === field) {
    sortDirection.value = sortDirection.value === "asc" ? "desc" : "asc";
  } else {
    sortField.value = field;
    sortDirection.value = "asc";
  }
};

const toggleExpanded = (key: string): void => {
  if (expandedKeys.has(key)) expandedKeys.delete(key);
  else expandedKeys.add(key);
};

interface FilterOption {
  key: string;
  country: string;
  registry: string | null;
  label: string;
}

const availableFilters = computed<FilterOption[]>(() => {
  const seen = new Map<string, FilterOption & { sortKey: string }>();
  for (const company of rawCompanies.value) {
    if (!company.country) continue;
    const key = filterKeyOf(company.country, company.registry);
    if (seen.has(key)) continue;
    const flag = getFlagEmoji(company.country);
    const country = translateCountryCode(company.country, t, te);
    const registry = company.registry
      ? ` · ${registryShortLabel(company.registry)}`
      : "";
    seen.set(key, {
      key,
      country: company.country,
      registry: company.registry,
      label: `${flag} ${country}${registry}`,
      sortKey: `${country}${registry}`,
    });
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.sortKey.localeCompare(b.sortKey)
  );
});

const visibleCompanies = computed(() => {
  let list = rawCompanies.value;

  if (countryFilter.value) {
    const [filterCountry, filterRegistry] = countryFilter.value.split("|");
    list = list.filter((c) => {
      if (c.country !== filterCountry) return false;
      // Empty registry segment means "all registries in this country" — bird's-eye sends
      // it for single-registry countries, and the user can pick the country-only entry
      // from the dropdown.
      if (filterRegistry) return c.registry === filterRegistry;
      return true;
    });
  }

  const query = search.value.trim().toLowerCase();
  if (query) {
    list = list.filter((c) => (c.name ?? "").toLowerCase().includes(query));
  }

  return sortCompanies(list, sortField.value, sortDirection.value);
});

const summary = computed(() => summarize(visibleCompanies.value));

const summarySegments = computed<StackedProgressBarSegment[]>(() => {
  // No scored devices in the current filter (e.g. only small/muted ones) — show a flat
  // neutral bar instead of a danger-red 0%, matching the company rows.
  if (summary.value.eligibleDeviceCount === 0) {
    return [{ key: "issued", value: 0, variant: "neutral" }];
  }
  const pct = summary.value.devicesIssuedPercent;
  return [
    {
      key: "issued",
      value: Math.min(100, pct),
      variant: pickVariant(pct),
    },
  ];
});

const fetchDashboard = async (): Promise<void> => {
  dashboardAbortController?.abort();
  const controller = new AbortController();
  dashboardAbortController = controller;
  loading.value = true;
  hasDashboardError.value = false;
  try {
    const response = await goSolidApi.get<IssuanceDashboardResponse>(
      "/api/admin/issuance/dashboard",
      {
        params: { year: year.value, month: month.value },
        signal: controller.signal,
      }
    );
    if (response.data) {
      rawCompanies.value = response.data.companies;
      expandedKeys.clear();
    } else {
      throw new Error("Error fetching issuance dashboard.");
    }
  } catch (error: unknown) {
    if (axios.isCancel(error)) return;
    hasDashboardError.value = true;
    globalErrorHandler(error);
  } finally {
    if (dashboardAbortController === controller) {
      dashboardAbortController = null;
      loading.value = false;
    }
  }
};

// Deliberately month-only: the export is a full platform-wide list, unaffected by the
// search/country/sort filters applied to the table above it.
const downloadDevicesWithoutIssuance = async (): Promise<void> => {
  exporting.value = true;
  try {
    const response = await goSolidApi.get(
      "/api/admin/issuance/devices-without-issuance/export",
      {
        params: { year: year.value, month: month.value },
        responseType: "blob",
      }
    );
    downloadFromApiResponse(
      response.data,
      response.headers,
      `soldera_devices_without_issuance_${year.value}-${String(month.value).padStart(2, "0")}-${String(new Date().getDate()).padStart(2, "0")}.xlsx`
    );
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    exporting.value = false;
  }
};

const onBirdseyeSelect = (selection: {
  country: string;
  registry: string | null;
  year: number;
  month: number;
}): void => {
  year.value = selection.year;
  month.value = selection.month;
  countryFilter.value = filterKeyOf(selection.country, selection.registry);
};

const fetchBirdseye = async (): Promise<void> => {
  birdseyeLoading.value = true;
  try {
    const response = await goSolidApi.get<IssuanceBirdseyeResponse>(
      "/api/admin/issuance/birdseye"
    );
    if (response.data) {
      birdseye.value = response.data;
    } else {
      throw new Error("Error fetching issuance birdseye.");
    }
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    birdseyeLoading.value = false;
  }
};

const fetchDocCountries = async (): Promise<void> => {
  try {
    docCountries.value = await fetchIssuanceDocCountries();
  } catch (error: unknown) {
    // Missing doc icons shouldn't take down the dashboard — or toast on every
    // mount while the docs source is unavailable. The per-country panel has
    // its own visible error state.
    console.error("Issuance doc countries unavailable", error);
  }
};

watch([year, month], fetchDashboard);
onMounted(() => {
  fetchDashboard();
  fetchBirdseye();
  fetchDocCountries();
});
</script>

<style lang="scss" scoped>
#IssuanceView {
  background: var(--phoenix-gray-100);
}

.letter-spacing-eyebrow {
  letter-spacing: 0.08em;
}

.tabular-nums {
  font-variant-numeric: tabular-nums;
}

.issuance-search {
  min-width: 240px;
  max-width: 340px;
}

.issuance-birdseye-flex-item {
  // Shrinkable (min-width: 0) so the grid's internal overflow-x scroller
  // engages instead of the whole page growing a horizontal scrollbar.
  min-width: 0;
  max-width: 100%;
}

.issuance-doc-panel {
  // Wraps under the grid instead of squeezing it when the viewport is narrow.
  min-width: min(100%, 26rem);
  flex-basis: 0;
}

.issuance-search__icon {
  left: 0.75rem;
}

.issuance-country-select {
  max-width: 220px;
}

.issuance-seg {
  display: inline-flex;
  background: #fff;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.375rem;
  padding: 2px;
}

.issuance-seg button {
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.3125rem 0.6875rem;
  border: none;
  background: transparent;
  color: var(--phoenix-gray-600);
  border-radius: 0.3125rem;
  cursor: pointer;
}

.issuance-seg button:hover {
  color: var(--phoenix-gray-900);
}

.issuance-seg button.active {
  background: var(--phoenix-gray-100);
  color: var(--phoenix-gray-1000, #0e1a14);
}

.issuance-table-header {
  display: grid;
  grid-template-columns: 1.25rem 9rem 1fr 6rem 6rem 9rem 24rem;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem 1rem;
}

.issuance-summary {
  background: #fff;
  border: 1px solid var(--phoenix-border-color);
  border-radius: 0.5rem;
  padding: 0.5rem 1.25rem;
}

.issuance-summary--loading {
  padding: 1.25rem;
}

.issuance-summary__row {
  display: grid;
  grid-template-columns: minmax(0, 12rem) 10.5rem 10.5rem 7rem 1fr auto;
  gap: 1.5rem;
  align-items: end;
  padding: 0.5rem 0;
  width: 100%;
}

.issuance-summary__metric {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.issuance-summary__bar {
  display: grid;
  grid-template-columns: 1fr 4rem;
  gap: 0.25rem 0.75rem;
  align-items: center;
}

.issuance-summary__bar-label {
  grid-column: 1 / -1;
  display: block;
}
</style>
